import React, { useState } from 'react';
import { Input, Button, Badge } from '@h2/ui';
import { Search, HelpCircle, X, Share2, Copy, Check } from 'lucide-react';
import { parseSearchQuery, SearchToken } from '../../hooks/useAdvancedSearch';

export interface SearchHelpItem {
  key: string;
  description: string;
  example: string;
}

export interface AdvancedSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helpItems?: SearchHelpItem[];
  className?: string;
  showTokens?: boolean;
  showShareButton?: boolean;
  onShare?: (searchUrl: string) => void;
}

export const AdvancedSearchInput: React.FC<AdvancedSearchInputProps> = ({
  value,
  onChange,
  placeholder = "검색어를 입력하세요...",
  helpItems = [],
  className = "",
  showTokens = true,
  showShareButton = true,
  onShare
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [copied, setCopied] = useState(false);
  const tokens = parseSearchQuery(value);

  const removeToken = (tokenToRemove: SearchToken) => {
    const newQuery = value
      .replace(tokenToRemove.original, '')
      .replace(/\s+/g, ' ')
      .trim();
    onChange(newQuery);
  };

  const addExample = (example: string) => {
    const newValue = value ? `${value} ${example}` : example;
    onChange(newValue);
    setShowHelp(false);
  };

  const handleShare = async () => {
    if (onShare) {
      // 외부에서 제공한 공유 함수 호출
      onShare(window.location.href);
    } else {
      // 기본 클립보드 복사
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('클립보드 복사 실패:', err);
      }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* 검색 입력 필드 */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {helpItems.length > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-10 w-10 p-0"
            onClick={() => setShowHelp(!showHelp)}
            title="검색 도움말"
          >
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}

        {showShareButton && value && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-10 w-10 p-0"
            onClick={handleShare}
            title={copied ? "복사됨!" : "검색 조건 공유"}
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Share2 className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        )}
      </div>

      {/* 파싱된 토큰 표시 */}
      {showTokens && tokens.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tokens.map((token, index) => (
            <Badge 
              key={index} 
              variant={token.type === 'key-value' ? 'default' : 'secondary'}
              className="text-sm flex items-center gap-1"
            >
              {token.type === 'key-value' && token.key ? (
                <span>
                  <span className="font-medium">{token.key}</span>: {token.value}
                </span>
              ) : (
                <span>{token.value}</span>
              )}
              <button
                onClick={() => removeToken(token)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* 도움말 패널 */}
      {showHelp && helpItems.length > 0 && (
        <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
          <div className="font-medium text-sm">검색 방법:</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {helpItems.map((item) => (
              <div key={item.key} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{item.key}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => addExample(item.example)}
                  >
                    사용
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">{item.description}</div>
                <code className="text-xs bg-background px-2 py-1 rounded">{item.example}</code>
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground pt-2 border-t">
            • 공백으로 구분하여 AND 조건으로 검색<br/>
            • 따옴표로 묶으면 공백이 포함된 값 검색 가능<br/>
            • 예: key:'separated value' 또는 key:"separated value"
          </div>
        </div>
      )}
    </div>
  );
};
